/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, takeEvery, select, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  Region,
  ActionFail,
  XError,
  RegionApi,
  Ducks,
  RegionStorage,
  LocalStorage,
  RegionSetup,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

export enum ActionTypes {
  REQUEST = 'region/REQUEST',
  SUCCESS = 'region/SUCCESS',
  FAIL = 'region/FAIL',
}

type Request = ActionRequest<ActionTypes.REQUEST>;
type Success = ActionSuccess<ActionTypes.SUCCESS, Region>;
type Fail = ActionFail<ActionTypes.FAIL>;

type Actions = Request | Success | Fail;

const Actions = {
  request: (): Request => ({ type: ActionTypes.REQUEST }),
  success: (payload: Region): Success => ({ type: ActionTypes.SUCCESS, payload }),
  fail: (error: XError): Fail => ({ type: ActionTypes.FAIL, error }),
};

export const Creators = {
  loadRegion: (): Request => Actions.request(),
};

export interface ReducerState {
  data?: Region;
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

const initialState: ReducerState = {};

export default function regionReducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.SUCCESS: {
      return {
        data: action.payload || [],
        processing: false,
        processed: true,
      };
    }
    case ActionTypes.FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };

    case ActionTypes.REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
      };

    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  isProcessed: (state: StoreState): boolean => state.region.processed === true,
  getError: (state: StoreState): XError | undefined => state.region.error,
  getRegion: (state: StoreState): Region | undefined => {
    return state.region?.data;
  },
  isEnabled: (state: StoreState): boolean => {
    // for OTT apps only OUT_OF_BAND is considered valid value for regionalization support
    return Ducks.Dms.Selectors.regionSetup(state) === RegionSetup.OUT_OF_BAND;
  },
};

// CACHE

function* saveRegionIdToCache(regionId: number | undefined): SagaIterator {
  if (regionId !== undefined) {
    const regionStorage = {
      id: regionId,
    };
    yield call(LocalStorage.storeRegion, regionStorage);
  }
}

function isRegionCacheValid(regionStorage: RegionStorage): boolean {
  if (regionStorage && regionStorage.id !== undefined) {
    return true;
  }
  return false;
}

function* getRegionIdFromCache(): SagaIterator {
  try {
    const regionStorage = yield call(LocalStorage.getRegion);

    if (isRegionCacheValid(regionStorage)) {
      return regionStorage.id;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

// SAGAS
function* getRegionFromApi(regionId: number | undefined): SagaIterator {
  const result: Region = yield Ducks.Api.callApi(RegionApi.regionList, regionId);
  return result;
}

function* getRegion(): SagaIterator {
  try {
    const cachedRegionId: number | undefined = yield call(getRegionIdFromCache);
    const newRegionId: number | undefined = yield select(Ducks.Household.Selectors.data.getRegionId);
    yield call(saveRegionIdToCache, newRegionId);
    const changed = newRegionId !== cachedRegionId;
    const region: Region = yield call(getRegionFromApi, newRegionId);
    region.changed = changed;

    yield put(Actions.success(region));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionTypes.REQUEST, getRegion)]);
}
